import { onMounted, nextTick, getCurrentInstance, ref, render, createVNode } from 'vue'
import { storeToRefs } from 'pinia'

import EventBus from '../event-bus/EventBus'
import { useAdaStore } from '../stores/ada'
import AdaSlotMarker from '../components/ad-slots/AdaSlotMarker.vue'

export function useAdaDynamicSlotMarker (teaserGroupType: string) {
  const instance = getCurrentInstance()
  const componentName = instance?.type.name ?? ''
  const getEl = (): HTMLElement => instance?.proxy?.$el

  const adaStore = useAdaStore()
  const { blockAds, isAsyncContentMode, dynamicAdSlotsAllocated } = storeToRefs(adaStore)
  const { setAsyncContentRefresh, flushAdSlotMarker, flushActivatedAdSlotMarker } = adaStore
  const markers = ref<HTMLElement[]>([])

  function checkParagraphTypeAndInjectMarkers () {
    if (componentName === 'ParagraphTypeHtml') {
      injectMarkerToHtmlParagraph()
    } else if (componentName === 'TeaserGroup') {
      injectMarkerToTeaserGroup()
    } else if (componentName === 'ParagraphTypeEpgSearch' && isAsyncContentMode.value) {
      injectMarkerToEpgSearch()
    } else if (componentName === 'ParagraphTypeVodSearch' && isAsyncContentMode.value) {
      injectMarkerToTeaserGroup(true)
    } else if (componentName === 'ParagraphTypeSearch' && isAsyncContentMode.value) {
      injectMarkerToTeaserGroup(true)
    }
  }

  function createAdaSlotMarker (child: Element, index: number, hasAsyncContent = false): HTMLElement {
    const container = document.createElement('div')
    // display:contents removes the box from layout so this wrapper is invisible to flex/grid
    container.style.display = 'contents'
    const vnode = createVNode(AdaSlotMarker, {
      aboveParagraph: {
        index: componentName + '-' + index,
        type: componentName,
        nested: true,
        hasAsyncContent,
        wordcount: (child as HTMLElement).innerText.split(' ').length
      },
      onRemove: () => removeAdaSlotMarker(container)
    })
    vnode.appContext = instance?.appContext ?? null
    render(vnode, container)
    markers.value.push(container)
    return container
  }

  function removeAdaSlotMarker (container: HTMLElement) {
    const index = markers.value.indexOf(container)
    if (index > -1) {
      markers.value.splice(index, 1)
      render(null, container) // unmount Vue component
      container.remove() // remove from DOM
    }
  }

  function injectMarkerToHtmlParagraph () {
    const EXCLUDE_POSITION_AFTER_TAGS = ['H1', 'H2', 'H3']
    const el = getEl()
    el.querySelectorAll(':scope > *').forEach((child, index) => {
      if (child.nextSibling && !EXCLUDE_POSITION_AFTER_TAGS.includes(child.tagName)) {
        const markerEl = createAdaSlotMarker(child, index)
        el.insertBefore(markerEl, child.nextSibling)
      }
    })
  }

  function injectMarkerToTeaserGroup (isAsyncContent = false) {
    // restrict to certain teaser group types - for AW we need this injection mostly for zodiac teaser
    // otherwise we also hijack teaser groups on overview pages where we don't want them
    const allowedTeaserGroupTypes = ['bx-teaser-container--info', 'bx-teaser-container--recipe', 'bx-teaser-container--lexicon', 'bx-teaser-container--vod']
    if (isAsyncContent) {
      // ParagraphTypeSearch TeaserGoup - but only addin this selector, while we are in async content mode for this paragraph type
      allowedTeaserGroupTypes.push('bx-teaser-container--one-third-width')
    }

    if (allowedTeaserGroupTypes.includes(teaserGroupType)) {
      injectMarkerToContainer(
        '.bx-teaser-container:not(.bx-js-prevent-slot-marker):not(.bx-teaser-container--dialog) .bx-teaser-container__wrapper',
        // "of S" counts positions only among non-favorite teasers, so the favorite doesn't skew the row count
        `.bx-teaser-container:not(.bx-js-prevent-slot-marker):not(.bx-teaser-container--dialog) .bx-teaser:nth-child(${getTeaserPerRow()}n of .bx-teaser:not(.bx-teaser--favorite))`,
        isAsyncContent
      )
    }
  }

  function getTeaserPerRow () {
    let counted = 0
    let referenceTop: number | null = null
    // favorite teasers span the full width, so they can't be used as a row reference
    const teaserList = Array.from(getEl().querySelectorAll('.bx-teaser:not(.bx-teaser--favorite)'))
    if (teaserList.length > 0) {
      // we only need to itterate through the teasers until we find a new row
      // to prevent unnecessary loops, we use for instead of forEach
      for (let i = 0; i < teaserList.length; i++) {
        const teaserTop = teaserList[i].getBoundingClientRect().top
        if (!referenceTop || teaserTop === referenceTop) {
          counted++
          referenceTop = teaserTop
        } else {
          break
        }
      }
    }
    return counted
  }

  function injectMarkerToEpgSearch () {
    injectMarkerToContainer(
      '.bx-epg-search__broadcasts',
      '.bx-epg-search__broadcasts .bx-epg-search__row',
      true // hasAsyncContent is true for EPG search as it loads content asynchronously
    )
  }

  function injectMarkerToContainer (wrapperSelector, childSelector, hasAsyncContent = false) {
    const el = getEl()
    const teaserWrapper = el.querySelector(wrapperSelector)
    el.querySelectorAll(childSelector).forEach((child, index) => {
      if (child.nextSibling) {
        const markerEl = createAdaSlotMarker(child, index, hasAsyncContent)
        teaserWrapper.insertBefore(markerEl, child.nextSibling)
      }
    })
  }

  /**
   * Injects the ad slot marker and allocates ad slots in dynamic content.
   * This method is called when dynamic content is loaded and ad slots need to be allocated.
   * It sets the async content refresh flag to true, flushes the ad slot marker,
   * and flushes the activated ad slot marker.
   * After the next tick, it sets the async content refresh flag to false,
   * calls the provided callback if any, and emits an event to notify that ad slots allocation can proceed.
   * @param {Function} [callback=false] - Optional callback function to be called after the ad slots are allocated.
   */
  function injectMarkerAndAllocateAdSlotsInDynamicContent (callback: (() => void) | false = false) {
    if (dynamicAdSlotsAllocated.value) {
      setAsyncContentRefresh(true)
      flushAdSlotMarker()
      flushActivatedAdSlotMarker()
    }

    nextTick(() => {
      setAsyncContentRefresh(false)
      if (callback) {
        callback()
      }
      EventBus.$emit('ad-loader:allocation')
    })
  }

  onMounted(() => {
    if (!blockAds.value && !isAsyncContentMode.value) {
      checkParagraphTypeAndInjectMarkers()
    }
  })

  return {
    injectMarkerAndAllocateAdSlotsInDynamicContent,
    injectMarkerToTeaserGroup,
    injectMarkerToEpgSearch
  }
}
